import React, { useState, useEffect, useRef } from 'react'
import { Select, PersonEditForm } from '../'
import { useDebounce, useModalDialog } from '../../hooks'
import { personService } from '../../services'

const toOption = (p) => ({
  value: p.person_uuid,
  label: p.person_name,
  secondaryLabel: [p.person_email, p.company_name, p.person_title].filter(Boolean).join(' • '),
})

// Multi-select sibling to PersonPicker — same server-search against
// /api/network/person/search, but accumulates a list of selected persons
// instead of a single uuid. The shared <Select> in isMulti mode always
// reports the single option that was just clicked, so `handleChange` here
// adds/removes it from `value` itself, same toggle pattern as network's
// NetworkingOverview.js filter bar / feedback's Analyze.js.
// allowCreate mirrors PersonPicker's "+ Skapa ... som ny person" option —
// same shared PersonEditForm modal, but on save the new person is added to
// the multi-selection instead of replacing a single value.
export const PersonMultiPicker = ({
  name,
  value,
  onChange,
  label,
  placeholder = 'Sök personer...',
  excludeInternal = false,
  // person_uuids to drop from the result list entirely — e.g. persons already
  // added to the event's guest list, so they can't be added a second time.
  excludeUuids = [],
  allowCreate = false,
}) => {
  const { showModalDialog, hideModalDialog, setLoadingModalDialog } = useModalDialog()
  const dialogRef = useRef({ submit: () => Promise.resolve() })

  const [searchTerm, setSearchTerm] = useState('')
  const [options, setOptions] = useState([])
  const [resultCount, setResultCount] = useState(0)
  const [loading, setLoading] = useState(false)

  const { debounce } = useDebounce((term) => runSearch(term), 250, [excludeInternal, excludeUuids.join(',')])

  const runSearch = (term) => {
    setLoading(true)
    personService.search(term)
      .then(res => {
        let persons = res.persons || []
        if (excludeInternal) persons = persons.filter(p => !p.is_internal)
        if (excludeUuids.length) persons = persons.filter(p => !excludeUuids.includes(p.person_uuid))
        setOptions(persons.map(toOption))
        setResultCount(res.resultCount || persons.length)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { runSearch('') }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (term) => {
    setSearchTerm(term)
    debounce(term)
  }

  const handleChange = ({ value: toggledUuid }) => {
    if (toggledUuid === '__create__') {
      openCreateDialog(searchTerm)
      return
    }
    const isSelected = value.some(v => v.value === toggledUuid)
    const next = isSelected
      ? value.filter(v => v.value !== toggledUuid)
      : [...value, options.find(o => o.value === toggledUuid)].filter(Boolean)
    onChange({ name, value: next })
  }

  const openCreateDialog = (term) => {
    showModalDialog({
      title: 'Skapa ny person',
      Content: () => (
        <PersonEditForm
          initialName={term}
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={(person) => {
            const option = toOption(person)
            setOptions(prev => prev.some(o => o.value === option.value) ? prev : [option, ...prev])
            onChange({ name, value: [...value, option] })
          }}
        />
      ),
      submitLabel: 'Skapa',
      submitLabelLoading: 'Skapar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  const createOption = allowCreate && searchTerm
    ? [{ value: '__create__', label: `+ Skapa "${searchTerm}" som ny person` }]
    : []

  return (
    <Select
      name={name}
      label={label}
      value={value}
      options={[...options, ...createOption]}
      searchTerm={searchTerm}
      loading={loading}
      resultCount={resultCount}
      onInputChange={handleInputChange}
      onChange={handleChange}
      isMulti
      showMultiLabel
      placeholder={placeholder}
    />
  )
}
